/**
 * EmailHtmlBody — renders an email's HTML body inside a sandboxed iframe.
 *
 * Email HTML is untrusted (see `sanitize-email-html.ts`). Rendering it via
 * `dangerouslySetInnerHTML` into the app DOM — as the chat renderer does for its
 * own narrow allow-list — is unsafe here: the email's CSS would restyle the host
 * app and remote resources would phone home. Instead we drop the sanitized
 * document into an iframe whose `sandbox` grants NO scripting, giving us CSS/DOM
 * isolation for free.
 *
 * `sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"`:
 *   - NO `allow-scripts` — the email can never run JS. Combined with the CSP's
 *     `default-src 'none'` and DOMPurify having stripped scripts, that's three
 *     independent script blocks.
 *   - `allow-same-origin` (without `allow-scripts`) is the safe half of the
 *     pair the spec warns about — it only lets the PARENT read the frame's
 *     scrollHeight to auto-size it; with no script in the frame there is nothing
 *     to abuse the same-origin grant.
 *   - `allow-popups*` lets hardened `target="_blank"` links open in a real tab.
 *
 * Inline images (`<img src="cid:…">`) are resolved to presigned attachment URLs
 * and rendered by default — they're message content, not tracker pixels. Remote
 * (`http`) images are still blocked until the reader opts in.
 */

import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { ImageOff } from 'lucide-react';
import { api } from '@/lib/api';
import type { EmailMailboxAttachmentDto } from '@/lib/api';
import { sanitizeEmailHtml, normalizeContentId } from '@/lib/sanitize-email-html';

interface EmailHtmlBodyProps {
  html: string;
  /** Message id, used to resolve inline (`cid:`) attachment URLs. */
  messageId: string;
  /** The message's attachments; inline ones are matched by Content-ID. */
  attachments?: EmailMailboxAttachmentDto[];
}

// Skip data-heavy "inline" parts — real inline images (logos, signatures) are
// small; a multi-MB part is almost certainly a regular attachment mis-flagged.
const MAX_INLINE_IMAGE_BYTES = 5 * 1024 * 1024;

// Stable reference for the default so the cid-resolution memo/effect below don't
// re-run on every render when no attachments are passed.
const NO_ATTACHMENTS: EmailMailboxAttachmentDto[] = [];

/** Collect the normalized Content-IDs the body references via `cid:`. */
function extractCidRefs(html: string): string[] {
  const refs = new Set<string>();
  const re = /(?:src|background)\s*=\s*["']?\s*cid:([^"'\s>)]+)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) refs.add(normalizeContentId(m[1]));
  return [...refs];
}

export function EmailHtmlBody({
  html,
  messageId,
  attachments = NO_ATTACHMENTS,
}: EmailHtmlBodyProps) {
  const [showImages, setShowImages] = useState(false);
  const [cidUrlMap, setCidUrlMap] = useState<Record<string, string>>({});
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [height, setHeight] = useState(0);

  // Inline attachments addressable by Content-ID (small enough to inline).
  const inlineByCid = useMemo(() => {
    const map = new Map<string, EmailMailboxAttachmentDto>();
    for (const a of attachments) {
      if (a.contentId && a.documentId && a.sizeBytes <= MAX_INLINE_IMAGE_BYTES) {
        map.set(normalizeContentId(a.contentId), a);
      }
    }
    return map;
  }, [attachments]);

  // Resolve the `cid:` images the body actually references into attachment URLs.
  useEffect(() => {
    const refs = extractCidRefs(html).filter((cid) => inlineByCid.has(cid));
    if (refs.length === 0) {
      // Keep the same reference when already empty — returning a fresh {} would
      // re-render and (with an unstable attachments prop) re-run this effect.
      setCidUrlMap((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        refs.map(async (cid) => {
          const att = inlineByCid.get(cid)!;
          try {
            const { url } = await api.getEmailMailboxAttachmentDownloadUrl(
              messageId,
              att.attachmentId,
              'inline',
            );
            return [cid, url] as const;
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      setCidUrlMap(Object.fromEntries(entries.filter((e): e is [string, string] => e !== null)));
    })();
    return () => {
      cancelled = true;
    };
  }, [html, messageId, inlineByCid]);

  const { document: srcDoc, hasRemoteImages } = useMemo(
    () => sanitizeEmailHtml(html, { blockRemoteImages: !showImages, cidUrlMap }),
    [html, showImages, cidUrlMap],
  );

  // Auto-size to content, and scale wide (typically fixed-width, table-based)
  // emails down so they fit the pane instead of growing an inner horizontal
  // scrollbar. `allow-same-origin` lets us read+style the framed document; a
  // ResizeObserver catches late layout shifts (e.g. images finishing load after
  // "Display images"). scrollWidth/scrollHeight are layout metrics unaffected by
  // the CSS transform, so re-measuring never oscillates.
  const handleLoad = useCallback(() => {
    const frame = iframeRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc?.documentElement || !doc.body) return;
    const docEl = doc.documentElement;
    const body = doc.body;

    const measure = () => {
      // The frame is sized to its content, so it never needs its own scrollbars.
      docEl.style.overflow = 'hidden';
      const available = frame.clientWidth;
      const contentWidth = docEl.scrollWidth;
      const scale = contentWidth > available && available > 0 ? available / contentWidth : 1;
      body.style.transformOrigin = 'top left';
      body.style.transform = scale < 1 ? `scale(${scale})` : '';
      setHeight(Math.ceil(docEl.scrollHeight * scale));
    };
    measure();

    observerRef.current?.disconnect();
    if (typeof ResizeObserver !== 'undefined') {
      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(docEl);
    }
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return (
    <div className="text-sm">
      {hasRemoteImages && !showImages && (
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <ImageOff className="h-3.5 w-3.5 shrink-0" />
            Remote images were blocked to protect your privacy.
          </span>
          <button
            type="button"
            onClick={() => setShowImages(true)}
            className="font-medium text-primary hover:underline"
          >
            Display images
          </button>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title="Email message"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        srcDoc={srcDoc}
        onLoad={handleLoad}
        className="w-full rounded border bg-white"
        style={{ height: height ? `${height}px` : '320px' }}
      />
    </div>
  );
}
